import {qs} from '@shared/helpers/queryString'
import {SchemasOrderDetail} from '@shared/schemas/order'

import {BannerAreaType} from '@web/core/constants'
import {RENTAL} from '@web/core/urls'
import getApi, {createApiAsyncThunk, createWrapper} from '@web/helpers/api'
import {changeBrowserLocation} from '@web/helpers/browser'
import {isMobile} from '@web/helpers/userAgent'
import {browserAlert} from '@web/middlewares/browserEventEmitter'
import {ProductDetailState, quantityClaimsNoSelector} from '@web/slices/productDetail/slice'

const api = getApi(`/orderApi/orderSheet/detail`)

export const initDetailByAPI = createApiAsyncThunk<
    {data: SchemasOrderDetail['/']['response']; isMobileDisplay: boolean},
    {
        orderNo: string
    }
>('detail/initDetailByAPI', async ({orderNo}) => {
    const response = await createWrapper<SchemasOrderDetail['/']['response']>(
        api.get('/', {
            params: {orderNo},
        }),
    )

    return {
        data: response,
        isMobileDisplay: isMobile,
    }
})

export const getAssignmentByAPI = createApiAsyncThunk<
    {data: SchemasOrderDetail['/assignments']['response']; isMobileDisplay: boolean},
    {orderNo: string},
    {state: {productDetail: ProductDetailState}}
>('detail/initAssignmentButtonByAPI', async ({orderNo}, {getState}) => {
    const state = getState()
    const claimNos = quantityClaimsNoSelector(state)
    const response = await createWrapper<SchemasOrderDetail['/assignments']['response']>(
        api.post('/assignments', {
            orderNo,
            claimNos,
        }),
    )

    return {
        data: response,
        isMobileDisplay: isMobile,
    }
})

export const initRefundByAPI = createApiAsyncThunk<
    SchemasOrderDetail['/refund']['response'],
    {
        orderNo: string
    }
>('detail/initRefundByAPI', async ({orderNo}) => {
    return await createWrapper<SchemasOrderDetail['/refund']['response']>(
        api.get('/refund', {
            params: {orderNo},
        }),
    )
})

export const initBannerByAPI = createApiAsyncThunk<
    SchemasOrderDetail['/externalBanner']['response']['responseData'],
    {
        /** 주문번호 */
        orderNo?: string
        /** 노출 영역 */
        exposureTypes: BannerAreaType[]
        /** return url */
        returnUrl?: string
        /** 예상포인트적립금 메타데이터 응답포함여부 */
        requireMetaData?: boolean
    }
>('detail/initBannerByAPI', async (params) => {
    const {responseData} = await createWrapper<SchemasOrderDetail['/externalBanner']['response']>(
        api.post('/externalBanner', params),
    )
    return responseData
})

/**
 * 최근결제수단 변경 action
 */
export const changeRecentPayMethod = createApiAsyncThunk<{status: string}, {payMethod: string}>(
    'detail/changeRecentPayMethod',
    async ({payMethod}) => {
        return await createWrapper<SchemasOrderDetail['/changeRecentPayMethod']['response']>(
            api.post('/changeRecentPayMethod', {
                payMethod,
            }),
        )
    },
)

/** 패밀리 결제 반려 */
export const rejectFamilyPay = createApiAsyncThunk<
    void,
    {
        orderNo: string
        productOrderNos: string[]
    },
    {
        rejectValue: {
            type: string
            title: string
            status: number
            detail: string
            instance: string
            defaultMessage: string
            code: string
        }
    }
>('detail/rejectFamilyPay', async ({orderNo, productOrderNos}) => {
    await createWrapper<SchemasOrderDetail['/reject']['response']>(
        api.post('/reject', {
            orderNo,
            productOrderNos,
        }),
    )
    return undefined
})

export const initRewardBenefitByAPI = createApiAsyncThunk<
    SchemasOrderDetail['/rewardBenefit']['response'],
    {orderNo: string}
>('detail/initRewardBenefitByAPI', async ({orderNo}) => {
    return createWrapper<SchemasOrderDetail['/rewardBenefit']['response']>(
        api.get('/rewardBenefit', {
            params: {orderNo},
        }),
    )
})

/**
 * 톡톡하기 임시말풍선 등록
 */
export const sendTalkTalkTempMessage = createApiAsyncThunk<
    SchemasOrderDetail['/sendTalkTalkTempMessage']['response'],
    {productOrderNo: string}
>('detail/sendTalkTalkTempMessage', async ({productOrderNo}) => {
    return createWrapper<SchemasOrderDetail['/sendTalkTalkTempMessage']['response']>(
        api.post('/sendTalkTalkTempMessage', {
            productOrderNo,
        }),
    )
})

/**
 * 계좌간편결제 입금 계좌 정보 조회
 */
export const getTradeAccountInfoByDetail = createApiAsyncThunk<
    {
        naverFinancialBankName: string
        naverFinancialMaskedAccountNo: string
        purchaserBankName: string
        purchaserMaskedAccountNo: string
    },
    string
>('detail/getTradeAccountInfo', async (orderNo) => {
    return createWrapper<SchemasOrderDetail['/tradeAccounts']['response']>(
        api.get('/tradeAccounts', {
            params: {orderNo},
        }),
    )
})

/** 렌탈 혜택정보 조회 */
export const getRentalBenefitDetail = createApiAsyncThunk<
    SchemasOrderDetail['/rental/benefit/detail']['response'],
    {
        contractNo: string
        demandDegreeCount: number
    }
>('detail/getRentalBenefit', async ({contractNo, demandDegreeCount}) => {
    return createWrapper<SchemasOrderDetail['/rental/benefit/detail']['response']>(
        api.get('/rental/benefit/detail', {
            params: {
                contractNo,
                demandDegreeCount,
            },
        }),
    )
})

/**
 * 반품철회 api 요청
 */
export const getReturnCancel = createApiAsyncThunk<
    SchemasOrderDetail['/returnCancel']['response'],
    {productOrderNoList: string[]}
>('detail/getReturnCancel', async ({productOrderNoList}) => {
    return createWrapper<SchemasOrderDetail['/returnCancel']['response']>(
        api.post('/returnCancel', {
            productOrderNoList,
        }),
    )
})

/**
 * 취소철회 api 요청
 */
export const getCancelCancel = createApiAsyncThunk<
    SchemasOrderDetail['/cancelCancel']['response'],
    {productOrderNoList: string[]}
>('detail/getCancelCancel', async ({productOrderNoList}) => {
    return createWrapper<SchemasOrderDetail['/cancelCancel']['response']>(
        api.post('/cancelCancel', {
            productOrderNoList,
        }),
    )
})

/**
 * 교환철회 api 요청
 */
export const getExchangeCancel = createApiAsyncThunk<
    SchemasOrderDetail['/exchangeCancel']['response'],
    {productOrderNoList: string[]}
>('detail/getExchangeCancel', async ({productOrderNoList}) => {
    return createWrapper<SchemasOrderDetail['/exchangeCancel']['response']>(
        api.post('/exchangeCancel', {
            productOrderNoList,
        }),
    )
})

/** 렌탈 계약 존재여부 조회 및 이동 */
export const pushToRentalDetail = createApiAsyncThunk<
    SchemasOrderDetail['/rental/basicInformation']['response'],
    {
        separateCode: string
        contractNo: string
        /** 렌탈상세  */
        returnUrl: string
    }
>('rental/moveRentalDetail', async ({separateCode, contractNo, returnUrl}, {dispatch}) => {
    const result = await createWrapper<SchemasOrderDetail['/rental/basicInformation']['response']>(
        api.get('/rental/basicInformation', {
            params: {
                separateCode,
                contractNo,
            },
        }),
    )
    if (result.errCode === '0000') {
        changeBrowserLocation({
            url: qs.stringifyUrl({
                url: `${RENTAL}/detail`,
                query: {
                    contractNo,
                    returnUrl,
                },
            }),
            type: 'PUSH',
        })
    } else {
        dispatch(
            browserAlert({
                message: '렌탈 계약정보가 생성중입니다. 최대 5분 정도 소요될 수 있으며, 완료 후 조회 가능합니다.',
            }),
        )
    }

    return result
})

export const repurchase = createApiAsyncThunk<
    SchemasOrderDetail['/repurchase']['response'],
    {
        productOrderNo: string
        trCode?: string
    }
>('detail/repurchase', async ({productOrderNo, trCode}) => {
    return createWrapper<SchemasOrderDetail['/repurchase']['response']>(
        api.post('/repurchase', {productOrderNo, trCode, device: isMobile ? 'MOBILE' : 'PC'}),
    )
})

export const repurchaseAddCart = createApiAsyncThunk<
    SchemasOrderDetail['/repurchase/cart']['response'],
    {
        productOrderNos: string[]
        trCode?: string
    }
>('detail/repurchaseCart', async ({productOrderNos, trCode}) => {
    return createWrapper<SchemasOrderDetail['/repurchase/cart']['response']>(
        api.post('/repurchase/cart', {productOrderNos, trCode, device: isMobile ? 'MOBILE' : 'PC'}),
    )
})
